import { index, modelOptions, mongoose, post, prop, PropType, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { E164PhoneNumber, getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    OutboundAiMessageFieldsWithDefaults,
    OutboundAiMessageSchemaTypes,
} from "../types/outboundAiMessage.d.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "outboundAiMessages" as const;

/** Mirrors the role `notificationRecords.messageType` plays, for agent-authored messages. */
export type AiMessageType = "agentReply" | "actionReport" | "approvalRequest" | "error";

export const AI_MESSAGE_TYPES = ["agentReply", "actionReport", "approvalRequest", "error"] as const;

/**
 * One tool the agent ran that changed something, with what it was given and what came back. Reads are
 * deliberately absent: they are re-derivable next turn, and a 200-row list would crowd out the actions
 * the agent has to remember to undo or build on.
 */
export type AgentToolCallRecord = {
    name: string;
    tier: number;
    input: unknown;
    /** Absent when the call threw; `error` carries the reason instead. */
    result?: unknown;
    error?: string;
};

/**
 * One action the agent took, as data rather than a sentence: which tool, which wording, and the values
 * that fill it. Stored untranslated so the action log can be re-rendered in whatever language the grower
 * is using when they open it, rather than frozen in the language of the round that produced it.
 *
 * `tier` mirrors the backend's `ToolTier`, which verdi-common cannot import.
 */
export type AgentAnnouncementRecord = {
    toolName: string;
    tier: 1 | 2 | 3;
    /** Picks between wordings of the same action. Absent for the default wording. */
    subKey?: string;
    /** A Date stays a Date through Mixed storage, so it is still formatted in the grower's zone on read. */
    params?: Record<string, string | number | Date>;
    failed?: boolean;
    /** The language the model was writing in, which the line is re-rendered in when it is still known. */
    locale?: string;
    includeFarmName?: boolean;
};

/**
 * One row per message the agent sends. A superset of `inboundSms`: the first four fields are identical
 * so the two merge into one transcript, and the rest records what produced the message.
 * Owner-scoped by number; never read from an org-facing surface.
 */
@index({ address: 1, sentAt: 1 })
@index({ inboundMessageSid: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "outboundAiMessages" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<OutboundAiMessage>>(...addSchemaKeyHook(SCHEMA_KEY))
export class OutboundAiMessage extends SchemaBase<OutboundAiMessage, OutboundAiMessageFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** E.164 number the message was sent to. Same field name as `notificationRecords.address`. */
    @validate({ zodValidator: E164PhoneNumber })
    @prop({ type: () => String, required: true })
    public address!: string;

    /** When it was sent. Same field name as `notificationRecords.sentAt`. */
    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public sentAt!: Date;

    /** The exact text sent. */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public body!: string;

    /** Twilio's message sid. Optional because a send can fail before one is assigned. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public messageSid?: string;

    /** Same role as `notificationRecords.messageType`, with the agent's own set of kinds. */
    @validate({ zodValidator: z.enum(AI_MESSAGE_TYPES) })
    @prop({ type: () => String, required: true })
    public messageType!: AiMessageType;

    /** `inboundSms.messageSid` this replies to. Absent for anything the agent sends unprompted. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public inboundMessageSid?: string;

    /** Model that produced the text, so a bad reply can be traced to what generated it. */
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public model?: string;

    /** Names of every tool that ran, reads included, in call order. The cheap audit trail. */
    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => [String], required: false }, PropType.ARRAY)
    public toolNames?: string[];

    /**
     * The write calls from this round, with input and result. Read back into the next round's context so
     * the agent can undo or build on what it just did instead of only knowing what it said.
     */
    @validate({ zodValidator: z.array(z.record(z.string(), z.unknown())).optional() })
    @trackMixed
    @prop({ type: () => [mongoose.Schema.Types.Mixed], required: false, allowMixed: Severity.ALLOW }, PropType.ARRAY)
    public toolCalls?: AgentToolCallRecord[];

    /**
     * What this round changed on the farm, in structured form. Kept untranslated: the action log renders
     * it at read time, so storing sentences would pin the log to the language of the round that wrote it.
     */
    @validate({ zodValidator: z.array(z.record(z.string(), z.unknown())).optional() })
    @trackMixed
    @prop({ type: () => [mongoose.Schema.Types.Mixed], required: false, allowMixed: Severity.ALLOW }, PropType.ARRAY)
    public announcements?: AgentAnnouncementRecord[];

    static getters = {} as const satisfies Record<string, (self: OutboundAiMessage) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<OutboundAiMessageSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<OutboundAiMessage, OutboundAiMessageFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

OutboundAiMessage.dtoValidators = getZodSchemaFromClass(
    OutboundAiMessage,
) as unknown as DtoValidatorTypes<OutboundAiMessageSchemaTypes>;
